/**
 * MobileApp Component for LOOPINO AI Platform
 * Main container component that integrates all mobile functionality
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import MobileFrame from './MobileFrame';
import MobileScreenRouter from './MobileScreenRouter';
import MobileSplashScreen from './MobileSplashScreen';
import { useMobileNavigation } from '../../hooks/useMobileNavigation';
import { shouldShowMobileInterface } from '../../utils/mobileDetection';
import useCompany from '../../hooks/useCompany';
import { useCapabilities } from '../../capabilities/CapabilityContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { MobileAlertProvider } from './common/alerts/MobileAlertProvider';

const DOCK_SCREENS = new Set([
  'posts',
  'calendar',
  'image-generator',
  'strategy',
  'social-hint',
  'aistorage'
]);

const normalizePostIdValue = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'object') {
    if (value.$oid) {
      return String(value.$oid);
    }

    if (value.id || value._id) {
      return normalizePostIdValue(value.id ?? value._id);
    }

    if (typeof value.toString === 'function') {
      const stringified = value.toString();
      if (stringified && stringified !== '[object Object]') {
        return String(stringified);
      }
    }
  }

  return null;
};

const getPostIdentifier = (postish) => {
  if (!postish) {
    return null;
  }

  if (typeof postish === 'string' || typeof postish === 'number') {
    return String(postish);
  }

  return (
    normalizePostIdValue(postish.id) ||
    normalizePostIdValue(postish._id) ||
    normalizePostIdValue(postish.post_id) ||
    normalizePostIdValue(postish.postId) ||
    null
  );
};

const getTargetPostIdFromUrl = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const params = new URLSearchParams(window.location.search || '');
    return params.get('postId') || params.get('targetPostId') || null;
  } catch (error) {
    console.error('[MobileApp] Unable to read target post id from URL', error);
    return null;
  }
};

const MobileApp = ({ 
  // Post management
  posts,
  onPostChange,
  onPostApprove,
  onPostReject,
  onPostRegenerate,
  onImageUpload,
  
  // App state
  isLoading = false,
  error = null,
  onRetry,
  
  // Header interactions
  onProfileClick,
  onSettingsClick,
  isRegenerating = false,
  
  // Campaign management
  campaignId,
  
  // Navigation
  initialScreen = 'posts'
}) => {
  const [isMobileDetected, setIsMobileDetected] = useState(() => shouldShowMobileInterface());
  const [hasSplashMinimumElapsed, setHasSplashMinimumElapsed] = useState(() => !shouldShowMobileInterface());
  const [isHomeReady, setIsHomeReady] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editingPostIndex, setEditingPostIndex] = useState(null);
  const [currentPostIndex, setCurrentPostIndex] = useState(0);
  const [lastViewedPostIndex, setLastViewedPostIndex] = useState(null);
  const [targetPostId, setTargetPostId] = useState(() => getTargetPostIdFromUrl());
  const [isFirstHomeVisit, setIsFirstHomeVisit] = useState(true);
  
  // AI Storage state
  const [aiStorageView, setAiStorageView] = useState('list');
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  // Prompt editor state
  const [promptEditorImageIndex, setPromptEditorImageIndex] = useState(0);
  
  // Social Hint state
  const [socialHintView, setSocialHintView] = useState('list');
  const [selectedSocialHint, setSelectedSocialHint] = useState(null);
  
  // Navigation hook for screen management
  const {
    currentScreen,
    navigateToScreen,
    isTransitioning,
    goBack,
    canGoBack,
    historyEntries
  } = useMobileNavigation(initialScreen);

  // Company hook to check DNA status
  const { currentCompany, hasCurrentCompany } = useCompany();
  const { getNavigationStateForScreen, isAccessible, isReady: isCapabilityReady } = useCapabilities();

  const canAccessScreen = useCallback((screen) => {
    if (!screen) {
      return false;
    }

    const capability = getNavigationStateForScreen(screen);
    if (!capability.managed || !isCapabilityReady) {
      return true;
    }

    return isAccessible(capability);
  }, [getNavigationStateForScreen, isAccessible, isCapabilityReady]);

  // Find next post to be published (in chronological order)
  const findNextPostToPublish = useCallback(() => {
    if (!posts || posts.length === 0) return 0;
    
    const now = new Date();
    const futurePosts = posts
      .map((post, index) => ({ post, index }))
      .filter(({ post }) => {
        const postDate = new Date(post.scheduledDate || post.scheduled_time || Date.now());
        return postDate > now && (post.status === 'pending' || post.status === 'approved');
      })
      .sort(({ post: a }, { post: b }) => {
        const dateA = new Date(a.scheduledDate || a.scheduled_time || Date.now());
        const dateB = new Date(b.scheduledDate || b.scheduled_time || Date.now());
        return dateA - dateB;
      });
    
    return futurePosts.length > 0 ? futurePosts[0].index : 0;
  }, [posts]);

  // DON'T initialize currentPostIndex here - let MobilePostReview handle it
  // This component only tracks the index for navigation between screens
  // MobilePostReview has its own logic to find the next post to publish

  // Handle edit post navigation
  const handleEditPost = useCallback((post) => {
    console.log('[MobileApp] Edit post triggered:', post);
    
    if (!post) {
      console.error('[MobileApp] No post provided to edit');
      return;
    }
    
    // Trova l'indice del post nell'array dei posts
    const postIndex = posts ? posts.findIndex(p => (p.id || p._id) === (post.id || post._id)) : -1;
    console.log('[MobileApp] Post index found:', postIndex, 'in', posts?.length || 0, 'posts');
    
    // Ensure post has the correct format for editing
    const formattedPost = {
      ...post,
      // Ensure we have images array (support both API and legacy format)
      images: post.media_urls || post.images || [],
      // Ensure we have description (support both API and legacy format)
      description: post.content || post.description || '',
      // Preserve both formats for compatibility
      content: post.content || post.description || '',
      media_urls: post.media_urls || post.images || []
    };
    
    console.log('[MobileApp] Formatted post for editing:', formattedPost);
    
    setEditingPost(formattedPost);
    setEditingPostIndex(postIndex);
    
    // Aggiorna anche l'indice del post corrente
    if (postIndex >= 0) {
      setCurrentPostIndex(postIndex);
    }
    
    navigateToScreen('post-edit');
  }, [navigateToScreen, posts]);

  // Handle close edit
  const handleCloseEdit = useCallback(() => {
    setEditingPost(null);
    setEditingPostIndex(null);
    navigateToScreen('posts');
  }, [navigateToScreen]);

  // Handle save edit
  const handleSaveEdit = useCallback(async (editedPost) => {
    try {
      if (onPostChange) {
        // Passa anche l'indice del post se disponibile
        if (currentPostIndex !== null && currentPostIndex >= 0) {
          await onPostChange(editedPost, currentPostIndex);
        } else {
          await onPostChange(editedPost);
        }
      }
      
      // Dopo il salvataggio, naviga al post specifico invece che alla home generica
      setEditingPost(null);
      setEditingPostIndex(null);
      // NON azzerare currentPostIndex - lo manteniamo per tornare al post corretto
      navigateToScreen('posts');
      
    } catch (error) {
      console.error('[MobileApp] Error saving post:', error);
      // Keep on edit page on error
    }
  }, [onPostChange, currentPostIndex, navigateToScreen]);

  // Check if mobile interface should be shown
  useEffect(() => {
    const checkMobile = () => {
      const shouldShow = shouldShowMobileInterface();
      setIsMobileDetected(shouldShow);
    };

    checkMobile();
    
    // Listen for window resize
    const handleResize = () => checkMobile();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const homeReadyRef = useRef(false);

  useEffect(() => {
    if (!isMobileDetected) {
      setHasSplashMinimumElapsed(true);
      setIsHomeReady(false);
      homeReadyRef.current = false;
      return;
    }

    setHasSplashMinimumElapsed(false);
    const timer = window.setTimeout(() => setHasSplashMinimumElapsed(true), 600);
    return () => window.clearTimeout(timer);
  }, [isMobileDetected]);
  const handleHomeReady = useCallback(() => {
    if (homeReadyRef.current) {
      return;
    }
    homeReadyRef.current = true;
    setIsHomeReady(true);
  }, []);

  // Handle screen navigation from dock
  const handleNavigationSideEffects = useCallback((fromScreen, toScreen) => {
    if (!toScreen) {
      return;
    }

    if (toScreen === 'posts') {
      const isAlreadyOnHome = fromScreen === 'posts';

      if (isFirstHomeVisit) {
        console.log('[MobileApp] First home visit - already initialized');
        setIsFirstHomeVisit(false);
      } else if (isAlreadyOnHome) {
        const nextPostIndex = findNextPostToPublish();
        console.log('[MobileApp] Click on home while on home - going to next post to publish:', nextPostIndex);
        setCurrentPostIndex(nextPostIndex);

        if (onPostChange && posts && posts[nextPostIndex]) {
          onPostChange(posts[nextPostIndex], nextPostIndex);
        }
      } else if (!isAlreadyOnHome && lastViewedPostIndex !== null) {
        console.log('[MobileApp] Returning to home - going to last viewed post:', lastViewedPostIndex);
        setCurrentPostIndex(lastViewedPostIndex);

        if (onPostChange && posts && posts[lastViewedPostIndex]) {
          onPostChange(posts[lastViewedPostIndex], lastViewedPostIndex);
        }
      }
    } else {
      if (fromScreen === 'posts') {
        console.log('[MobileApp] Leaving home - saving current post index:', currentPostIndex);
        setLastViewedPostIndex(currentPostIndex);
      }

      if (fromScreen === 'strategy' && toScreen !== 'strategy') {
        selectedCampaignIdRef.current = null;
        setSelectedCampaignIdFromPost(null);
      }
    }
  }, [findNextPostToPublish, isFirstHomeVisit, lastViewedPostIndex, onPostChange, posts, currentPostIndex]);

  const handleScreenChange = useCallback((screen, options = {}) => {
    if (!screen) {
      return;
    }

    if (!canAccessScreen(screen)) {
      console.log('[MobileApp] Blocked navigation to absent capability-managed screen:', screen);
      return;
    }

    const { source = 'internal', addToHistory = true } = options;

    if (screen === currentScreen) {
      handleNavigationSideEffects(currentScreen, screen);
      navigateToScreen(screen, { addToHistory, source });
      return;
    }

    if (screen === 'new-campaign') {
      console.log('[MobileApp] Navigating to new-campaign from:', currentScreen);
      setNewCampaignSourceScreen(currentScreen);
    }

    handleNavigationSideEffects(currentScreen, screen);
    navigateToScreen(screen, { addToHistory, source });
  }, [canAccessScreen, currentScreen, handleNavigationSideEffects, navigateToScreen]);

  const historyLength = historyEntries.length;
  const previousEntry = useMemo(() => (
    historyLength > 1 ? historyEntries[historyEntries.length - 2] : null
  ), [historyEntries, historyLength]);

  const previousScreen = previousEntry?.screen ?? null;
  const currentEntrySource = historyEntries[historyEntries.length - 1]?.source ?? 'internal';

  const canSwipeBack = useMemo(() => {
    if (!previousEntry || !canGoBack) {
      return false;
    }

    const currentIsDock = DOCK_SCREENS.has(currentScreen);
    if (!currentIsDock) {
      return true;
    }

    return currentEntrySource !== 'dock';
  }, [previousEntry, canGoBack, currentScreen, currentEntrySource]);

  const handleBackNavigation = useCallback(() => {
    if (!previousEntry || !canGoBack) {
      return;
    }

    console.log('[MobileApp] Gesture back navigation from', currentScreen, 'to', previousEntry.screen);
    handleNavigationSideEffects(currentScreen, previousEntry.screen);
    goBack();
  }, [previousEntry, canGoBack, currentScreen, handleNavigationSideEffects, goBack]);

  const handlePlansBack = useCallback(() => {
    if (previousEntry) {
      handleBackNavigation();
      return;
    }

    handleScreenChange('battery');
  }, [handleBackNavigation, handleScreenChange, previousEntry]);

  // Handle header interactions with logging
  const handleProfileClick = useCallback((companyData) => {
    if (typeof onProfileClick === 'function') {
      onProfileClick(companyData);
    }
  }, [onProfileClick]);

  const handleSettingsClick = useCallback(() => {
    navigateToScreen('settings');
  }, [navigateToScreen]);

  // Handle settings back navigation
  const handleSettingsBack = useCallback(() => {
    handleScreenChange('posts');
  }, [handleScreenChange]);

  // Handle profile back navigation
  const handleProfileBack = useCallback(() => {
    handleScreenChange('posts');
  }, [handleScreenChange]);

  // Handle company profile navigation from settings
  const handleCompanyProfileClick = useCallback(() => {
    navigateToScreen('profile');
  }, [navigateToScreen]);

  // Handle battery navigation
  const handleBatteryClick = useCallback(() => {
    // Navigation is handled by MobileHeader via onNavigate (MobileFrame onScreenChange).
  }, []);

  // Handle battery back navigation
  const handleBatteryBack = useCallback(() => {
    handleScreenChange('posts');
  }, [handleScreenChange]);

  const handleOpenPlans = useCallback(() => {
    handleScreenChange('subscription-plans', { source: 'battery-card' });
  }, [handleScreenChange]);

  // Open prompt editor from PostEditPage
  const handleOpenPromptEditor = useCallback((imageIndex = 0, updatedPost = null) => {
    if (updatedPost) {
      setEditingPost(prev => {
        if (!prev) {
          return updatedPost;
        }
        return {
          ...prev,
          ...updatedPost
        };
      });
    }

    setPromptEditorImageIndex(typeof imageIndex === 'number' ? imageIndex : 0);
    navigateToScreen('prompt-editor');
  }, [navigateToScreen, setEditingPost]);

  // Handle calendar post click - naviga alla home con il post selezionato
  const handleCalendarPostClick = useCallback((post, postIndex, normalizedId = null) => {
    console.log('[MobileApp] Calendar post clicked:', post, 'index:', postIndex);

    const resolvedId = normalizedId || getPostIdentifier(post);
    let resolvedIndex = -1;

    if (posts && posts.length > 0 && resolvedId) {
      resolvedIndex = posts.findIndex(p => getPostIdentifier(p) === resolvedId);
    }

    if (resolvedIndex < 0 && typeof postIndex === 'number' && postIndex >= 0) {
      resolvedIndex = postIndex;
    }

    if (resolvedIndex >= 0) {
      setCurrentPostIndex(resolvedIndex);
    }

    if (resolvedId) {
      setTargetPostId(resolvedId);
    }
    // Evita l'auto-jump al prossimo post nella prima visita quando arrivo dal calendario
    setIsFirstHomeVisit(false);

    // Naviga alla schermata posts (home) con il post selezionato
    navigateToScreen('posts');

    // Non notificare il parent qui per evitare override che causano jump/flicker
  }, [navigateToScreen, posts]);

  // State per tracciare la data target del calendario
  const [calendarTargetDate, setCalendarTargetDate] = useState(null);

  // Handle thumbnail click - naviga al calendario nella settimana del post
  const handleThumbnailClick = useCallback((post) => {
    console.log('[MobileApp] Thumbnail clicked, navigating to calendar for post:', post);

    // Estrai la data del post
    const postDate = post?.scheduledDate || post?.scheduled_time;
    if (postDate) {
      setCalendarTargetDate(new Date(postDate));
    }

    // Naviga al calendario
    navigateToScreen('calendar');
  }, [navigateToScreen]);

  // State per tracciare la campagna selezionata da navigazione
  // Usa un ref per non causare re-render
  const selectedCampaignIdRef = useRef(null);
  const [selectedCampaignIdFromPost, setSelectedCampaignIdFromPost] = useState(null);

  // State per tracciare da dove si è arrivati alla pagina new-campaign
  const [newCampaignSourceScreen, setNewCampaignSourceScreen] = useState('strategy');

  // Handle campaign click - naviga alla pagina strategia
  const handleCampaignClick = useCallback((post) => {
    // Estrai l'ID della campagna dal post
    const campaignIdToSelect = post?.campaignId || post?.campaign_id;

    // Normalizza l'ID (potrebbe essere un oggetto MongoDB)
    const normalizedCampaignId = campaignIdToSelect?.$oid ||
                                 (typeof campaignIdToSelect === 'object' ? JSON.stringify(campaignIdToSelect) : campaignIdToSelect);

    // Salva l'ID sia nel ref che nello state
    selectedCampaignIdRef.current = normalizedCampaignId;
    setSelectedCampaignIdFromPost(normalizedCampaignId);

    // Naviga alla pagina strategia
    navigateToScreen('strategy');
  }, [navigateToScreen]);

  // Handle AI Storage back navigation
  const handleAIStorageBack = useCallback(() => {
    if (aiStorageView === 'detail') {
      // If in detail view, go back to list
      setAiStorageView('list');
      setSelectedProjectId(null);
    } else {
      // If in list view, go back to home
      handleScreenChange('posts');
    }
  }, [aiStorageView, handleScreenChange]);

  // Handle Social Hint navigation
  const handleSocialHintBack = useCallback((view, hint = null) => {
    console.log('[MobileApp] Social Hint navigation:', view, hint);
    
    if (view === 'detail' && hint) {
      // Navigate to detail view with selected hint (stay on same screen)
      setSocialHintView('detail');
      setSelectedSocialHint(hint);
    } else if (view === 'detail-image' && hint) {
      // Navigate to image detail view with selected hint (stay on same screen)
      setSocialHintView('detail-image');
      setSelectedSocialHint(hint);
    } else if (view === 'list') {
      // Navigate back to list view (stay on same screen)
      setSocialHintView('list');
      setSelectedSocialHint(null);
    } else {
      // Default: go back to home
      setSocialHintView('list');
      setSelectedSocialHint(null);
      handleScreenChange('posts');
    }
  }, [handleScreenChange]);

  // Handle logout navigation
  const handleLogout = useCallback(() => {
    console.log('[MobileApp] Logout requested, navigating to login screen');
    navigateToScreen('login');
  }, [navigateToScreen]);

  // Handle login success navigation
  const handleLoginSuccess = useCallback(() => {
    console.log('[MobileApp] Login successful, navigating to posts screen');
    navigateToScreen('posts');
  }, [navigateToScreen]);

  // Handle new campaign back navigation - context-aware
  const handleNewCampaignBack = useCallback(() => {
    console.log('[MobileApp] Navigating back from new campaign to:', newCampaignSourceScreen);
    navigateToScreen(newCampaignSourceScreen);
  }, [navigateToScreen, newCampaignSourceScreen]);

  // Handle campaign created callback
  const handleCampaignCreated = useCallback((newCampaign) => {
    console.log('[MobileApp] Campaign created successfully:', newCampaign);
    // Navigate to posts page to see the newly generated posts
    navigateToScreen('posts');
  }, [navigateToScreen]);

  // Handle navigate to DNA (from empty state - no DNA)
  const handleNavigateToDna = useCallback(() => {
    console.log('[MobileApp] Navigating to DNA profile from empty state');
    navigateToScreen('profile');
  }, [navigateToScreen]);

  const handleNavigateToImageGenerator = useCallback(() => {
    console.log('[MobileApp] Navigating to image generator from empty state');
    handleScreenChange('image-generator');
  }, [handleScreenChange]);

  // Handle create first campaign (from empty state - DNA exists)
  const handleCreateFirstCampaign = useCallback(() => {
    console.log('[MobileApp] Creating first campaign from empty state');

    // DNA check is already done in MobilePostReview, so we can proceed directly
    // If we're here, it means the DNA exists and we can create a campaign
    setNewCampaignSourceScreen('posts');
    navigateToScreen('strategy'); // Navigate to strategy page which shows empty state with create campaign button
  }, [navigateToScreen]);

  // Handle logo click to go home (same logic as home button)
  const handleLogoClick = useCallback(() => {
    handleScreenChange('posts');
  }, [handleScreenChange]);

  // Handle post interactions with logging
  const handlePostChange = useCallback((post, index) => {
    
    // Aggiorna l'indice del post corrente quando l'utente naviga
    if (typeof index === 'number' && index >= 0) {
      setCurrentPostIndex(index);
    }
    
    if (onPostChange) {
      onPostChange(post, index);
    }
  }, [onPostChange]);

  const handlePostApprove = useCallback(async (post, index) => {
    if (onPostApprove) {
      return await onPostApprove(post, index);
    }
  }, [onPostApprove]);

  const handlePostReject = useCallback(async (post, index) => {
    if (onPostReject) {
      return await onPostReject(post, index);
    }
  }, [onPostReject]);

  const handlePostRegenerate = useCallback(async (post, type, options = {}) => {
    if (type === 'image' && options?.response) {
      const previewSources = [];

      if (Array.isArray(options.response.regenerated_images)) {
        previewSources.push(...options.response.regenerated_images.filter(Boolean));
      }

      const regeneratedContentMedia = options.response.regenerated_content?.media_urls;
      if (Array.isArray(regeneratedContentMedia)) {
        previewSources.push(...regeneratedContentMedia.filter(Boolean));
      }

      if (typeof options.response.image_url === 'string') {
        previewSources.push(options.response.image_url);
      }

      const metadataImages = options.response.regeneration_metadata?.generated_images;
      if (Array.isArray(metadataImages)) {
        metadataImages.forEach((item) => {
          const candidate = item?.url || item?.image_url || item?.public_url || item?.source_url;
          if (candidate) {
            previewSources.push(candidate);
          }
        });
      }

      const uniquePreviewSources = Array.from(new Set(previewSources));
      if (uniquePreviewSources.length > 0) {
        const targetIndex = typeof options.imageIndex === 'number' && options.imageIndex >= 0
          ? options.imageIndex
          : 0;

        setEditingPost(prev => {
          if (!prev) return prev;

          const updatedImages = Array.isArray(prev.images) ? [...prev.images] : [];
          while (updatedImages.length <= targetIndex) {
            updatedImages.push(null);
          }
          updatedImages[targetIndex] = uniquePreviewSources[0];

          const updatedMediaUrls = Array.isArray(prev.media_urls) ? [...prev.media_urls] : [...updatedImages];
          while (updatedMediaUrls.length <= targetIndex) {
            updatedMediaUrls.push(null);
          }
          updatedMediaUrls[targetIndex] = uniquePreviewSources[0];

          const normalizeMediaValue = (value) => {
            if (!value) {
              return null;
            }
            if (typeof value === 'string') {
              return value;
            }
            if (typeof value === 'object') {
              return (
                value.url ||
                value.previewUrl ||
                value.imageUrl ||
                value.image_url ||
                value.src ||
                value.source ||
                null
              );
            }
            return null;
          };

          const generateMediaId = (index) => `media-${index}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

          const baseMediaArray = (() => {
            if (Array.isArray(prev.mediaArray) && prev.mediaArray.length > 0) {
              return prev.mediaArray.map((item, idx) => {
                const fallbackUrl =
                  normalizeMediaValue(item?.url) ||
                  normalizeMediaValue(item?.previewUrl) ||
                  normalizeMediaValue(prev.media_urls?.[idx]) ||
                  normalizeMediaValue(prev.images?.[idx]);

                return {
                  ...item,
                  id: item?.id || generateMediaId(idx),
                  type: item?.type || 'url',
                  url: item?.url || fallbackUrl,
                  previewUrl: item?.previewUrl || fallbackUrl
                };
              });
            }

            const sourceArray = (Array.isArray(prev.media_urls) && prev.media_urls.length > 0)
              ? prev.media_urls
              : (Array.isArray(prev.images) ? prev.images : []);

            return sourceArray.map((entry, idx) => {
              const fallbackUrl = normalizeMediaValue(entry);
              return {
                id: generateMediaId(idx),
                type: 'url',
                url: fallbackUrl,
                previewUrl: fallbackUrl
              };
            });
          })();

          const updatedMediaArray = [...baseMediaArray];
          const minimumLength = Math.max(updatedImages.length, targetIndex + 1);
          for (let idx = updatedMediaArray.length; idx < minimumLength; idx += 1) {
            const fallbackUrl = normalizeMediaValue(updatedImages[idx]);
            updatedMediaArray.push({
              id: generateMediaId(idx),
              type: 'url',
              url: fallbackUrl,
              previewUrl: fallbackUrl
            });
          }

          if (updatedMediaArray.length > updatedImages.length) {
            updatedMediaArray.splice(updatedImages.length);
          }

          const existingMediaItem = updatedMediaArray[targetIndex] || {};

          const promptJsonFromResponse =
            options?.response?.regenerated_content?.prompt_json ||
            options?.response?.regeneration_metadata?.prompt_json ||
            (Array.isArray(options?.response?.regeneration_metadata?.generated_images)
              ? options.response.regeneration_metadata.generated_images[0]?.prompt_json
              : null);

          const promptTextFromResponse =
            options?.response?.regenerated_content?.prompt_text ||
            options?.response?.regeneration_metadata?.prompt_text ||
            null;

          const promptJsonFromPayload = options?.payload?.prompt_json || null;
          const promptTextFromPayload =
            options?.payload?.prompt_text ||
            options?.payload?.prompt ||
            null;

          const promptJson = promptJsonFromResponse || promptJsonFromPayload || existingMediaItem?.prompt_json || existingMediaItem?.metadata?.prompt_json || null;
          const promptText = promptTextFromResponse || promptTextFromPayload || existingMediaItem?.prompt_text || existingMediaItem?.metadata?.prompt_text || null;
          const promptJsonSchema = options?.payload?.prompt_json_schema || existingMediaItem?.metadata?.prompt_json_schema || null;

          const nextMetadata = existingMediaItem?.metadata ? { ...existingMediaItem.metadata } : {};
          if (promptJson) {
            nextMetadata.prompt_json = promptJson;
          }
          if (promptText) {
            nextMetadata.prompt_text = promptText;
          }
          if (promptJsonSchema) {
            nextMetadata.prompt_json_schema = promptJsonSchema;
          }

          const mergedMediaItem = {
            ...existingMediaItem,
            id: existingMediaItem.id || generateMediaId(targetIndex),
            type: 'url',
            url: uniquePreviewSources[0],
            previewUrl: uniquePreviewSources[0],
            source: 'regenerate-preview'
          };

          if (promptJson) {
            mergedMediaItem.prompt_json = promptJson;
          }
          if (promptText) {
            mergedMediaItem.prompt_text = promptText;
          }
          if (Object.keys(nextMetadata).length > 0) {
            mergedMediaItem.metadata = nextMetadata;
          }

          updatedMediaArray[targetIndex] = mergedMediaItem;

          return {
            ...prev,
            images: updatedImages,
            media_urls: updatedMediaUrls,
            mediaArray: updatedMediaArray,
            providedThumbnail: targetIndex === 0 ? uniquePreviewSources[0] : prev.providedThumbnail,
            __pendingRegeneratedImages: null,
            __pendingRegeneratedImageIndex: null,
            __pendingRegeneratedResponse: null,
            __pendingRegeneratedPrompt: null,
            __hasPendingRegeneratedChanges: true
          };
        });
      }
    }

    if (onPostRegenerate) {
      return await onPostRegenerate(post, type, options);
    }

    return options?.response || null;
  }, [onPostRegenerate]);

  // Handle retry for error states
  const handleRetry = useCallback(() => {
    if (onRetry) {
      onRetry();
    }
  }, [onRetry]);

  // Handle pull-to-refresh
  const handleRefresh = useCallback(async () => {
    console.log('[MobileApp] Pull-to-refresh triggered');
    
    try {
      // Se esiste una funzione di retry personalizzata, usala
      if (onRetry) {
        await onRetry();
      } else {
        // Fallback: ricarica la finestra dopo un breve delay per un feedback migliore
        await new Promise(resolve => setTimeout(resolve, 500));
        window.location.reload();
      }
    } catch (error) {
      console.error('[MobileApp] Error during refresh:', error);
      // In caso di errore, proviamo comunque a ricaricare
      window.location.reload();
    }
  }, [onRetry]);

  const sharedScreenRouterProps = useMemo(() => ({
    posts,
    targetPostId,
    onPostChange: handlePostChange,
    onPostApprove: handlePostApprove,
    onPostReject: handlePostReject,
    onPostRegenerate: handlePostRegenerate,
    onEditPost: handleEditPost,
    onCampaignClick: handleCampaignClick,
    onThumbnailClick: handleThumbnailClick,
    onImageUpload,
    isLoading,
    editingPost,
    onCloseEdit: handleCloseEdit,
    onSaveEdit: handleSaveEdit,
    campaignId,
    selectedCampaignIdFromPost,
    onOpenPromptEditor: handleOpenPromptEditor,
    promptEditorImageIndex,
    onSettingsBack: handleSettingsBack,
    onProfileBack: handleProfileBack,
    onProfileSettingsClick: handleSettingsClick,
    onCompanyProfileClick: handleCompanyProfileClick,
    onBatteryBack: handleBatteryBack,
    onOpenPlans: handleOpenPlans,
    onPlansBack: handlePlansBack,
    onCalendarPostClick: handleCalendarPostClick,
    calendarTargetDate,
    onAIStorageBack: handleAIStorageBack,
    aiStorageView,
    selectedProjectId,
    onSocialHintBack: handleSocialHintBack,
    socialHintView,
    selectedSocialHint,
    onLogout: handleLogout,
    onLoginSuccess: handleLoginSuccess,
    onNewCampaignBack: handleNewCampaignBack,
    onCampaignCreated: handleCampaignCreated,
    onNavigateToDna: handleNavigateToDna,
    onNavigateToImageGenerator: handleNavigateToImageGenerator,
    onCreateFirstCampaign: handleCreateFirstCampaign
  }), [
    aiStorageView,
    calendarTargetDate,
    campaignId,
    editingPost,
    handleBatteryBack,
    handlePlansBack,
    handleOpenPlans,
    handleCalendarPostClick,
    handleCampaignClick,
    handleCloseEdit,
    handleCompanyProfileClick,
    handleEditPost,
    handleAIStorageBack,
    handleLoginSuccess,
    handleNavigateToDna,
    handleNavigateToImageGenerator,
    handleNewCampaignBack,
    handleOpenPromptEditor,
    handlePostApprove,
    handlePostChange,
    handlePostReject,
    handlePostRegenerate,
    handleProfileBack,
    handleSettingsBack,
    handleSettingsClick,
    handleSocialHintBack,
    handleThumbnailClick,
    handleSaveEdit,
    handleLogout,
    handleCampaignCreated,
    handleCreateFirstCampaign,
    onImageUpload,
    posts,
    promptEditorImageIndex,
    selectedCampaignIdFromPost,
    selectedProjectId,
    selectedSocialHint,
    socialHintView,
    targetPostId,
    isLoading
  ]);

  // Don't render if not mobile (let desktop handle it)
  if (!isMobileDetected) {
    return null;
  }

  const shouldShowSplash = !hasSplashMinimumElapsed || !isHomeReady;

  return (
    <LanguageProvider>
      <MobileAlertProvider>
        <MobileFrame
          currentScreen={currentScreen}
          previousScreen={previousScreen}
          onScreenChange={handleScreenChange}
          isLoading={isLoading && !posts}
          error={error}
          onRetry={handleRetry}
          onRefresh={handleRefresh}
          onProfileClick={handleProfileClick}
          onBatteryClick={handleBatteryClick}
          onLogoClick={handleLogoClick}
          isRegenerating={isRegenerating}
          canSwipeBack={canSwipeBack && !isTransitioning}
          onSwipeBack={handleBackNavigation}
          isTransitioning={isTransitioning}
        >
          <MobileScreenRouter
            {...sharedScreenRouterProps}
            currentScreen={currentScreen}
            onScreenChange={handleScreenChange}
            onHomeReady={handleHomeReady}
          />
        </MobileFrame>
        {shouldShowSplash && <MobileSplashScreen />}
      </MobileAlertProvider>
    </LanguageProvider>
  );
};

export default MobileApp;
